/**
 * Client Service - Fixed Endpoints
 * Handles all API calls for client management (tenants and landlords)
 */

import { MeterFormData } from '@/components/meters/MeterForm';
import { api_service } from './api';
import {
  Client,
  ClientCreateData,
  ClientUpdateData,
  ClientFilters,
  Property,
  PropertyCreateData,
  PropertyUpdateData,
  ClientActivity,
  ClientStatistics,
  ClientBalance,
  ClientStatement,
  ClientAccountSummary,
  ClientContact,
  BulkClientUpdate,
  BulkClientStatusUpdate,
  ClientValidation,
  ClientImportResult,
  ClientSearchResult
} from '@/types/client_types';
import { Meter } from '@/types/meter_types';

// ============ TYPE ALIASES FOR COMPATIBILITY ============

export type CreateClientPayload = ClientCreateData;

export interface ClientFinancialSummary {
  total_invoices: number;
  total_invoiced_amount: number;
  total_paid_amount: number;
  current_balance: number;
  overdue_balance: number;
  last_invoice_date?: string;
  last_payment_date?: string;
  payment_trend: Array<{
    month: string;
    amount: number;
  }>;
}

export interface ClientConsumptionSummary {
  active_meters: number;
  total_consumption_current_month: number;
  total_consumption_last_month: number;
  average_monthly_consumption: number;
  highest_consumption_month: {
    month: string;
    consumption: number;
  };
  consumption_trend: Array<{
    month: string;
    consumption: number;
  }>;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ============ CLIENT CRUD OPERATIONS ============

/**
 * Fetch paginated clients with optional filters
 */
export const get_clients = async (
  page: number = 1,
  page_size: number = 20,
  filters?: ClientFilters
): Promise<PaginatedResponse<Client>> => {
  try {
    // FIXED: Changed from '/clients/' to '/clients/clients/'
    const response = await api_service.get('/clients/clients/', {
      params: {
        page,
        page_size,
        ...filters
      }
    });
    return response.data || response;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

/**
 * Fetch all clients without pagination (for dropdowns)
 */
export const get_all_clients = async (
  client_type?: 'landlord' | 'tenant'
): Promise<Client[]> => {
  try {
    // FIXED: Changed from '/clients/all/' to '/clients/clients/all/'
    const response = await api_service.get('/clients/clients/all/', {
      params: { client_type }
    });
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching all clients:', error);
    throw error;
  }
};

/**
 * Fetch all clients with optional filters (legacy method for compatibility)
 */
export const fetch_clients = async (
  filters?: ClientFilters
): Promise<Client[]> => {
  try {
    // FIXED: Changed from '/clients/' to '/clients/clients/'
    const response = await api_service.get('/clients/clients/', {
      params: filters
    });
    
    const data = response.data || response;
    
    // If paginated response, extract results
    if (data && typeof data === 'object' && 'results' in data) {
      return Array.isArray(data.results) ? data.results : [];
    }
    
    // If direct array
    if (Array.isArray(data)) {
      return data;
    }
    
    console.error('Unexpected response format:', data);
    return [];
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

/**
 * Fetch a single client by ID
 */
export const get_client = async (client_id: number): Promise<Client> => {
  try {
    // FIXED: Changed from '/clients/{id}/' to '/clients/clients/{id}/'
    const response = await api_service.get(`/clients/clients/${client_id}/`);
    return response.data || response;
  } catch (error) {
    console.error('Error fetching client:', error);
    throw error;
  }
};

export const fetch_client = async (client_id: number): Promise<Client> => {
  return get_client(client_id);
};

/**
 * Create a new client
 */
export const create_client = async (
  data: CreateClientPayload
): Promise<Client> => {
  try {
    // FIXED: Changed from '/clients/' to '/clients/clients/'
    const response = await api_service.post('/clients/clients/', data);
    return response.data || response;
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
};

/**
 * Update an existing client
 */
export const update_client = async (
  client_id: number,
  data: ClientUpdateData
): Promise<Client> => {
  try {
    // FIXED: Changed from '/clients/{id}/' to '/clients/clients/{id}/'
    const response = await api_service.patch(`/clients/clients/${client_id}/`, data);
    return response.data || response;
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

/**
 * Delete a client
 */
export const delete_client = async (client_id: number): Promise<void> => {
  try {
    // FIXED: Changed from '/clients/{id}/' to '/clients/clients/{id}/'
    await api_service.delete(`/clients/clients/${client_id}/`);
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

// ============ CLIENT PROPERTIES ============

export const fetch_client_properties = async (
  client_id: number
): Promise<Property[]> => {
  try {
    // FIXED: Changed from '/clients/{id}/properties/' to '/clients/clients/{id}/properties/'
    const response = await api_service.get(`/clients/clients/${client_id}/properties/`);
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching client properties:', error);
    throw error;
  }
};

export const create_property = async (
  data: PropertyCreateData
): Promise<Property> => {
  try {
    // FIXED: Changed from '/properties/' to '/clients/properties/'
    const response = await api_service.post('/clients/properties/', data);
    return response.data || response;
  } catch (error) {
    console.error('Error creating property:', error);
    throw error;
  }
};

export const update_property = async (
  property_id: number,
  data: PropertyUpdateData
): Promise<Property> => {
  try {
    // FIXED: Changed from '/properties/{id}/' to '/clients/properties/{id}/'
    const response = await api_service.patch(`/clients/properties/${property_id}/`, data);
    return response.data || response;
  } catch (error) {
    console.error('Error updating property:', error);
    throw error;
  }
};

export const delete_property = async (property_id: number): Promise<void> => {
  try {
    // FIXED: Changed from '/properties/{id}/' to '/clients/properties/{id}/'
    await api_service.delete(`/clients/properties/${property_id}/`);
  } catch (error) {
    console.error('Error deleting property:', error);
    throw error;
  }
};

export const fetch_all_properties = async (
  landlord_id?: number
): Promise<Property[]> => {
  try {
    // FIXED: Changed from '/properties/' to '/clients/properties/'
    const response = await api_service.get('/clients/properties/', {
      params: { landlord_id }
    });
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching properties:', error);
    throw error;
  }
};

// ============ CLIENT ACTIVITIES ============

export const fetch_client_activities = async (
  client_id: number,
  limit?: number
): Promise<ClientActivity[]> => {
  try {
    // FIXED: Changed from '/clients/{id}/activities/' to '/clients/clients/{id}/activities/'
    const response = await api_service.get(`/clients/clients/${client_id}/activities/`, {
      params: { limit }
    });
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching client activities:', error);
    throw error;
  }
};

export const add_client_activity = async (
  client_id: number,
  activity: {
    activity_type: string;
    description: string;
    amount?: number;
    reference?: string;
  }
): Promise<ClientActivity> => {
  try {
    // FIXED: Changed from '/clients/{id}/activities/' to '/clients/clients/{id}/activities/'
    const response = await api_service.post(
      `/clients/clients/${client_id}/activities/`,
      activity
    );
    return response.data || response;
  } catch (error) {
    console.error('Error adding client activity:', error);
    throw error;
  }
};

// ============ CLIENT FINANCIAL SUMMARIES ============

export const fetch_client_financial_summary = async (
  client_id: number
): Promise<ClientFinancialSummary> => {
  try {
    // FIXED: Changed from '/clients/{id}/financial-summary/' to '/clients/clients/{id}/financial-summary/'
    const response = await api_service.get(`/clients/clients/${client_id}/financial-summary/`);
    return response.data || response;
  } catch (error) {
    console.error('Error fetching client financial summary:', error);
    throw error;
  }
};

export const fetch_client_consumption_summary = async (
  client_id: number
): Promise<ClientConsumptionSummary> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(
      `/clients/clients/${client_id}/consumption_summary/`
    );
    return response.data || response;
  } catch (error) {
    console.error('Error fetching client consumption summary:', error);
    throw error;
  }
};

// Add these to your meter_service.ts

/**
 * Create a new meter
 */
export const create_meter = async (data: MeterFormData): Promise<Meter> => {
  try {
    const response = await api_service.post('/meters/meters/', data);
    return response.data || response;
  } catch (error) {
    console.error('Error creating meter:', error);
    throw error;
  }
};

/**
 * Update an existing meter
 */
export const update_meter = async (
  meter_id: string,
  data: MeterFormData
): Promise<Meter> => {
  try {
    const response = await api_service.patch(`/meters/meters/${meter_id}/`, data);
    return response.data || response;
  } catch (error) {
    console.error('Error updating meter:', error);
    throw error;
  }
};

export const fetch_client_account_summary = async (
  client_id: number
): Promise<ClientAccountSummary> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(`/clients/clients/${client_id}/account-summary/`);
    return response.data || response;
  } catch (error) {
    console.error('Error fetching client account summary:', error);
    throw error;
  }
};

export const fetch_client_balance = async (
  client_id: number
): Promise<ClientBalance> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(`/clients/clients/${client_id}/balance/`);
    return response.data || response;
  } catch (error) {
    console.error('Error fetching client balance:', error);
    throw error;
  }
};

// ============ CLIENT STATEMENTS ============

export const generate_client_statement = async (
  client_id: number,
  date_from: string,
  date_to: string
): Promise<ClientStatement> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(`/clients/clients/${client_id}/statement/`, {
      params: { date_from, date_to }
    });
    return response.data || response;
  } catch (error) {
    console.error('Error generating client statement:', error);
    throw error;
  }
};

export const download_client_statement_pdf = async (
  client_id: number,
  date_from: string,
  date_to: string
): Promise<Blob> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(`/clients/clients/${client_id}/statement/pdf/`, {
      params: { date_from, date_to },
      responseType: 'blob'
    });
    return response.data || response;
  } catch (error) {
    console.error('Error downloading client statement:', error);
    throw error;
  }
};

// ============ CLIENT STATISTICS ============

export const get_client_stats = async (): Promise<ClientStatistics> => {
  try {
    // FIXED: Changed from '/clients/statistics/' to '/clients/clients/statistics/'
    const response = await api_service.get('/clients/clients/statistics/');
    return response.data || response;
  } catch (error) {
    console.error('Error fetching client statistics:', error);
    throw error;
  }
};

export const fetch_client_statistics = async (): Promise<ClientStatistics> => {
  return get_client_stats();
};

// ============ CLIENT SEARCH ============

export const search_clients = async (
  query: string
): Promise<ClientSearchResult[]> => {
  try {
    // FIXED: Changed from '/clients/search/' to '/clients/clients/search/'
    const response = await api_service.get('/clients/clients/search/', {
      params: { q: query }
    });
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error searching clients:', error);
    throw error;
  }
};

// ============ CLIENT VALIDATION ============

export const validate_client_id_number = async (
  id_number: string
): Promise<ClientValidation> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.post('/clients/clients/validate-id/', { id_number });
    return response.data || response;
  } catch (error) {
    console.error('Error validating client ID:', error);
    throw error;
  }
};

export const check_phone_number = async (
  phone_number: string
): Promise<{ exists: boolean; client?: Client }> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.post('/clients/clients/check-phone/', { phone_number });
    return response.data || response;
  } catch (error) {
    console.error('Error checking phone number:', error);
    throw error;
  }
};

// ============ BULK OPERATIONS ============

export const bulk_update_clients = async (
  data: BulkClientUpdate
): Promise<{ updated: number; clients: Client[] }> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.post('/clients/clients/bulk-update/', data);
    return response.data || response;
  } catch (error) {
    console.error('Error bulk updating clients:', error);
    throw error;
  }
};

export const bulk_update_status = async (
  client_ids: number[],
  is_active: boolean
): Promise<{ updated: number }> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.post('/clients/clients/bulk-status/', {
      client_ids,
      is_active
    });
    return response.data || response;
  } catch (error) {
    console.error('Error bulk updating client status:', error);
    throw error;
  }
};

export const bulk_update_client_status = async (
  data: BulkClientStatusUpdate
): Promise<{ updated: number }> => {
  return bulk_update_status(data.client_ids, data.is_active);
};

// ============ CLIENT IMPORT/EXPORT ============

export const import_clients = async (
  file: File
): Promise<ClientImportResult> => {
  try {
    const form_data = new FormData();
    form_data.append('file', file);

    // FIXED: Changed endpoint path
    const response = await api_service.post('/clients/clients/import/', form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data || response;
  } catch (error) {
    console.error('Error importing clients:', error);
    throw error;
  }
};

export const export_clients_csv = async (
  filters?: ClientFilters
): Promise<Blob> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get('/clients/clients/export/csv/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data || response;
  } catch (error) {
    console.error('Error exporting clients to CSV:', error);
    throw error;
  }
};

export const export_clients_excel = async (
  filters?: ClientFilters
): Promise<Blob> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get('/clients/clients/export/excel/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data || response;
  } catch (error) {
    console.error('Error exporting clients to Excel:', error);
    throw error;
  }
};

export const export_clients_pdf = async (
  filters?: ClientFilters
): Promise<Blob> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get('/clients/clients/export/pdf/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data || response;
  } catch (error) {
    console.error('Error exporting clients to PDF:', error);
    throw error;
  }
};

// ============ CLIENT CONTACT MANAGEMENT ============

export const record_client_contact = async (
  contact: ClientContact
): Promise<ClientContact> => {
  try {
    // FIXED: Changed from '/clients/contacts/' to '/clients/contacts/'
    const response = await api_service.post('/clients/contacts/', contact);
    return response.data || response;
  } catch (error) {
    console.error('Error recording client contact:', error);
    throw error;
  }
};

export const fetch_client_contacts = async (
  client_id: number
): Promise<ClientContact[]> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(`/clients/clients/${client_id}/contacts/`);
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching client contacts:', error);
    throw error;
  }
};

// ============ LANDLORD SPECIFIC OPERATIONS ============

export const fetch_landlords = async (): Promise<Client[]> => {
  try {
    // FIXED: Changed from '/clients/landlords/' to '/clients/clients/landlords/'
    const response = await api_service.get('/clients/clients/landlords/');
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching landlords:', error);
    throw error;
  }
};

export const fetch_landlord_tenants = async (
  landlord_id: number
): Promise<Client[]> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(`/clients/clients/${landlord_id}/tenants/`);
    const data = response.data || response;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching landlord tenants:', error);
    throw error;
  }
};

export const fetch_landlord_consolidated_invoice = async (
  landlord_id: number,
  period: string
): Promise<any> => {
  try {
    // FIXED: Changed endpoint path
    const response = await api_service.get(
      `/clients/clients/${landlord_id}/consolidated-invoice/`,
      {
        params: { period }
      }
    );
    return response.data || response;
  } catch (error) {
    console.error('Error fetching landlord consolidated invoice:', error);
    throw error;
  }
};


/**
 * Export clients in the specified format
 */
export const export_clients = async (
  format: 'csv' | 'excel' | 'pdf',
  filters?: ClientFilters
): Promise<Blob> => {
  switch (format) {
    case 'csv':
      return export_clients_csv(filters);
    case 'excel':
      return export_clients_excel(filters);
    case 'pdf':
      return export_clients_pdf(filters);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

// ============ DEFAULT EXPORT ============
export const client_service = {
  get_clients,
  get_all_clients,
  fetch_clients,
  get_client,
  fetch_client,
  create_client,
  update_client,
  delete_client,
  search_clients,
  get_client_stats,
  fetch_client_statistics,
  bulk_update_status,
  bulk_update_client_status,
  fetch_client_properties,
  fetch_client_activities,
  fetch_client_financial_summary,
  fetch_client_consumption_summary,
  fetch_client_account_summary,
  fetch_client_balance,
  generate_client_statement,
  download_client_statement_pdf,
  validate_client_id_number,
  check_phone_number,
  bulk_update_clients,
  import_clients,
  export_clients,        
  export_clients_csv,
  export_clients_excel,
  export_clients_pdf,
  record_client_contact,
  fetch_client_contacts,
  fetch_landlords,
  fetch_landlord_tenants,
  fetch_landlord_consolidated_invoice,
  create_property,
  update_property,
  delete_property,
  fetch_all_properties
};
export default client_service;